import { useMediaUploadMutation } from "./useMediaUploadMutation";
import { useMediaUploadMultipleMutation } from "./useMediaUploadMultipleMutation";
import { useMediaDeleteTemporaryMutation } from "./useMediaDeleteTemporaryMutation";
import { useMediaDeleteMutation } from "./useMediaDeleteMutation";

interface MediaItem {
  file?: File;
  url?: string;
  media_id?: string;
  filename?: string;
  metadata?: Record<string, unknown>;
  uploadedAt?: Date | string;
  isTemporary?: boolean;
}

/**
 * Composable that provides upload and delete functions for media files.
 * Returns functions compatible with UiFileUpload component props.
 * 
 * @example
 * ```vue
 * <script setup>
 * import { useMediaUploadIntegration } from "@shopexperts/shared";
 * 
 * const { uploadFn, deleteFn } = useMediaUploadIntegration();
 * </script>
 * 
 * <template>
 *   <UiFileUpload
 *     :upload-fn="uploadFn"
 *     :delete-fn="deleteFn"
 *     collection="my_collection"
 *   />
 * </template>
 * ```
 */
export function useMediaUploadIntegration() {
  const uploadMutation = useMediaUploadMutation();
  const uploadMultipleMutation = useMediaUploadMultipleMutation();
  const deleteTemporaryMutation = useMediaDeleteTemporaryMutation();
  const deleteMutation = useMediaDeleteMutation();

  /**
   * Upload a single file to the media service.
   * @param file - The file to upload
   * @param collection - The collection name for organizing media
   * @param metadata - Optional metadata to attach to the upload
   * @returns Object with id and url of the uploaded media
   */
  async function uploadFn(
    file: File,
    collection: string,
    metadata?: object
  ) {
    const response = await uploadMutation.mutateAsync({
      file,
      collection,
      metadata: metadata as Record<string, unknown>,
    });
    
    return {
      id: response.id,
      url: response.cdn_url,
      filename: response.filename,
      metadata: response.metadata || undefined,
    };
  }

  /**
   * Delete a media item. Handles both temporary and permanent media.
   * @param item - The media item to delete (must have media_id)
   */
  async function deleteFn(item: MediaItem) {
    if (!item.media_id) {
      console.warn("[useMediaUploadIntegration] Cannot delete item without media_id");
      return;
    }

    if (item.isTemporary) {
      await deleteTemporaryMutation.mutateAsync(item.media_id);
    } else {
      await deleteMutation.mutateAsync(item.media_id);
    }
  }

  /**
   * Upload multiple files at once.
   * @param files - Array of files to upload
   * @param collection - The collection name for organizing media
   * @param metadata - Optional metadata to attach to all uploads
   * @returns Array of objects with id and url for each uploaded media
   */
  async function uploadMultipleFn(
    files: File[],
    collection: string,
    metadata?: object
  ) {
    const response = await uploadMultipleMutation.mutateAsync({
      files,
      collection,
      metadata: metadata as Record<string, unknown>,
    });
    
    return response.map((media) => ({
      id: media.id,
      url: media.cdn_url,
      filename: media.filename,
      metadata: media.metadata || undefined,
    }));
  }

  return {
    uploadFn,
    deleteFn,
    uploadMultipleFn,
    isUploading: uploadMutation.isPending,
    isUploadingMultiple: uploadMultipleMutation.isPending,
    isDeleting: deleteTemporaryMutation.isPending || deleteMutation.isPending,
  };
}
